import axios from 'axios';
import { auth } from '@/lib/firebase';

const API_URL = window.location.hostname === 'localhost' 
  ? 'http://localhost:8001/api/admin'
  : (process.env.REACT_APP_API_URL || 'https://api-jtgorgdsma-uc.a.run.app/api/admin');

const getAuthToken = async () => {
  return new Promise((resolve) => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      unsubscribe();
      if (user) {
        const token = await user.getIdToken();
        resolve(token);
      } else {
        resolve(null);
      }
    });
  });
};

const createAPI = () => {
  const api = axios.create({
    baseURL: API_URL,
  });

  api.interceptors.request.use(async (config) => {
    const token = await getAuthToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return api;
};

const api = createAPI();

export const getDashboardStats = async () => {
  const response = await api.get('/dashboard-stats');
  return response.data;
};

export const getRevenue = async (timeRange = '6months') => {
  const response = await api.get('/revenue', { params: { timeRange } });
  return response.data;
};

export const getInvoices = async () => {
  const response = await api.get('/invoices');
  return response.data;
};

export const getActivities = async () => {
  const response = await api.get('/activities');
  return response.data;
};

export const getNotifications = async () => {
  const response = await api.get('/notifications');
  return response.data;
};

export const getContent = async () => {
  const response = await api.get('/content');
  return response.data;
};

export const getWorkers = async () => {
  const response = await api.get('/workers');
  return response.data;
};

export const verifyWorker = async (id, isVerified) => {
    const response = await api.put(`/workers/${id}/verify`, { isVerified });
    return response.data;
};

export const getUsers = async (role, limit = 50, lastId = null) => {
  const params = { role, limit };
  if (lastId) params.lastId = lastId;
  const response = await api.get('/users', { params });
  console.log(response.data);
  
  return response.data;
};

// Fetch ALL users of a given role by paging through the backend pagination.
// Avoids the arbitrary 1000-record cap that made some users unfindable in search.
// pageSize defaults to 1000 to match the old single-request size (so databases up
// to 1000 users cost exactly one request — no regression); larger sets are paged
// until fully loaded, with a safety cap and termination guards against cursor races.
export const getAllUsers = async (role, pageSize = 1000) => {
  const MAX_PAGES = 25; // safety cap (1000 * 25 = 25,000 users)
  const allUsers = [];
  const seen = new Set();
  let totalCount = 0;
  let lastId = null;
  let pages = 0;

  while (pages < MAX_PAGES) {
    const data = await getUsers(role, pageSize, lastId);
    if (!data || !data.success) break;

    const users = data.users || [];
    if (users.length === 0) break; // no more data

    // De-duplicate defensively: if a doc is deleted mid-pagination the backend's
    // startAfter() cursor restarts from the first page, which would otherwise
    // duplicate rows (and could loop forever). De-dup + progress guard cover that.
    let added = 0;
    for (const u of users) {
      const key = u._id !== undefined ? u._id : u.id;
      if (key !== undefined) {
        if (seen.has(key)) continue;
        seen.add(key);
      }
      allUsers.push(u);
      added++;
    }

    const newLastId = data.pagination && data.pagination.lastId;
    if (added === 0 || newLastId === lastId) break; // no progress → stop
    lastId = newLastId;
    totalCount = data.totalCount || allUsers.length;
    pages++;

    if (data.pagination && data.pagination.hasMore === false) break; // no more pages on server
  }

  return { success: true, users: allUsers, totalCount };
};

export const createUser = async (userData) => {
    const response = await api.post('/users', userData);
    return response.data;
};

export const updateUser = async (id, userData) => {
    const response = await api.put(`/users/${id}`, userData);
    return response.data;
};

export const deleteUser = async (id) => {
    const response = await api.delete(`/users/${id}`);
    return response.data;
};

export const getPickups = async (limit = 50, lastId = null, date = null) => {
    const params = { limit };
    if (lastId) params.lastId = lastId;
    if (date) params.date = date;
    const response = await api.get('/pickups', { params });
    return response.data;
};

export const createAdminPickup = async (data) => {
    const response = await api.post('/pickups/create', data);
    return response.data;
};

export const updatePickupStatus = async (id, data) => {
    const response = await api.put(`/pickups/${id}/status`, data);
    return response.data;
};

export const deletePickup = async (id) => {
    const response = await api.delete(`/pickups/${id}`);
    return response.data;
};

export const getProfile = async () => {
    const response = await api.get('/me');
    return response.data;
};

export const assignSubscription = async (data) => {
    const response = await api.post('/subscriptions/assign', data);
    return response.data;
};

export const removeSubscription = async (subscriptionId) => {
    const response = await api.delete(`/subscriptions/active/${subscriptionId}`);
    return response.data;
};

export const sendNotification = async (data) => {
    const response = await api.post('/notifications/send', data);
    return response.data;
};

export const createSubscriptionPlan = async (data) => {
    const response = await api.post('/subscriptions', data);
    return response;
};

export const getSubscriptionPlans = async () => {
    const response = await api.get('/subscriptions');
    return response;
};

export const deleteSubscriptionPlan = async (id) => {
    const response = await api.delete(`/subscriptions/${id}`);
    return response;
};

export const updateSubscriptionPlan = async (id, data) => {
    const response = await api.put(`/subscriptions/${id}`, data);
    return response;
};

export const getTimeSlots = async () => {
  const response = await api.get('/slots');
  return response.data;
};

export const updateTimeSlot = async (id, data) => {
  const response = await api.put(`/slots/${id}`, data);
  return response.data;
};

export const createSlot = async (data) => {
  const response = await api.post('/slots', data);
  return response.data;
};

export const getWasteItems = async () => {
    const response = await api.get('/waste-items');
    return response.data;
};

export const createWasteItem = async (data) => {
    const response = await api.post('/waste-items', data);
    return response.data;
};

export const updateWasteItem = async (id, data) => {
    const response = await api.put(`/waste-items/${id}`, data);
    return response.data;
};

export const deleteWasteItem = async (id) => {
    const response = await api.delete(`/waste-items/${id}`);
    return response.data;
};

export const getServiceAreas = async () => {
    const response = await api.get('/service-areas');
    return response.data;
};

export const createServiceArea = async (data) => {
    const response = await api.post('/service-areas', data);
    return response.data;
};

export const deleteServiceArea = async (id) => {
    const response = await api.delete(`/service-areas/${id}`);
    return response.data;
};

export const getAccountCategories = async () => {
    const response = await api.get('/account-categories');
    return response.data;
};

export const createAccountCategory = async (data) => {
    const response = await api.post('/account-categories', data);
    return response.data;
};

export const updateAccountCategory = async (id, data) => {
    const response = await api.put(`/account-categories/${id}`, data);
    return response.data;
};

export const deleteAccountCategory = async (id) => {
    const response = await api.delete(`/account-categories/${id}`);
    return response.data;
};

export const getSystemSettings = async () => {
    const response = await api.get('/system-settings');
    return response.data;
};

export const updateSystemSettings = async (data) => {
  const response = await api.put('/system-settings', data);
  return response.data;
};

export const getAppUpdates = async () => {
    const response = await api.get('/app-updates');
    return response.data;
};

export const createAppUpdate = async (data) => {
    const response = await api.post('/app-updates', data);
    return response.data;
};

export const addRecurringCollection = async (pickupId, data) => {
  const response = await api.post(`/pickups/${pickupId}/add-collection`, data);
  return response.data;
};

export const getPickupHistory = async (pickupId) => {
  const response = await api.get(`/pickups/${pickupId}/history`);
  return response.data;
};

export const deleteCollectionRecord = async (collectionId) => {
  const response = await api.delete(`/pickups/collections/${collectionId}`);
  return response.data;
};

export const updateCollectionStatus = async (collectionId, status) => {
  const response = await api.put(`/pickups/collections/${collectionId}/status`, { status });
  return response.data;
};

export const getDailyCollections = async (date) => {
    const response = await api.get('/daily-collections', { params: { date } });
    return response.data;
};

export const getDisposalSites = async () => {
    const response = await api.get('/disposal-sites');
    return response.data;
};

export const createDisposalSite = async (data) => {
    const response = await api.post('/disposal-sites', data);
    return response.data;
};

export const updateDisposalSiteStatus = async (id, status) => {
    const response = await api.put(`/disposal-sites/${id}/status`, { status });
    return response.data;
};

export const optimizeFleetDailyRoute = async (data) => {
    const response = await api.post('/fleet-optimize-daily-route', data);
    return response.data;
};

export const getAssignedRoutes = async (date) => {
    const response = await api.get('/pickups/assign-route', { params: { date } });
    return response.data;
};

export const assignDailyRoute = async (data) => {
    const response = await api.post('/pickups/assign-route', data);
    return response.data;
};

export const deleteAssignedRoute = async (id) => {
    const response = await api.delete(`/pickups/assign-route/${id}`);
    return response.data;
};

// The geocoding proxy is mounted at /api/geocode (not under /api/admin), so it
// needs the admin suffix stripped from the configured API base URL.
const getGeocodeBaseUrl = () =>
  window.location.hostname === 'localhost'
    ? 'http://localhost:8001/api'
    : (process.env.REACT_APP_API_URL || 'https://api-jtgorgdsma-uc.a.run.app/api/admin').replace(/\/admin$/, '');

// Resolve an address / place string to [lat, lng] via the backend geocoding
// proxy (Google Maps under the hood). Returns null when nothing is found.
export const geocodeAddressQuery = async (query) => {
  const trimmed = query ? String(query).trim() : '';
  if (!trimmed) return null;

  try {
    const response = await fetch(`${getGeocodeBaseUrl()}/geocode?q=${encodeURIComponent(trimmed)}`);
    if (!response.ok) return null;

    const results = await response.json();
    if (!Array.isArray(results) || results.length === 0) return null;

    const lat = parseFloat(results[0].lat);
    const lng = parseFloat(results[0].lon);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    return [lat, lng];
  } catch (error) {
    console.error('Geocoding request failed:', error);
    return null;
  }
};

export default api;
